import { useEffect, useRef } from 'react';

const MAX_ZONE = 96;
const MIN_ZONE = 32;
const ZONE_RATIO = 0.2;
const MIN_STEP = 4;
const MAX_STEP = 18;
// The drag-and-drop processing model fires dragover about every 350ms for a
// stationary pointer, so anything at or under that would stall a held drag.
const STALE_MS = 600;

type Edges = { top: number; bottom: number };

const isScrollable = (el: HTMLElement) => {
  const overflowY = getComputedStyle(el).overflowY;
  return (
    (overflowY === 'auto' || overflowY === 'scroll') &&
    el.scrollHeight > el.clientHeight
  );
};

const findScroller = (from: HTMLElement | null): HTMLElement | null => {
  let el = from;
  while (el) {
    if (isScrollable(el)) return el;
    if (el === document.body) break;
    el = el.parentElement;
  }
  const doc = document.scrollingElement as HTMLElement | null;
  return doc && isScrollable(doc) ? doc : null;
};

// The document scroller's own rect is the whole document, not the viewport.
const edgesOf = (el: HTMLElement): Edges => {
  if (el === document.scrollingElement) {
    return { top: 0, bottom: window.innerHeight };
  }
  const rect = el.getBoundingClientRect();
  return { top: rect.top, bottom: rect.bottom };
};

const stepFor = (depth: number, zone: number) =>
  MIN_STEP + (MAX_STEP - MIN_STEP) * Math.min(1, depth / zone);

/**
 * Scrolls the list's scroll container while a drag hovers near its top or bottom
 * edge. Chrome does this natively only within a band a few pixels deep, which is
 * too small to aim at once the container is a modal body.
 */
export const useDragAutoScroll = (
  rootRef: React.RefObject<HTMLElement | null>,
  isDragActive: () => boolean
) => {
  const activeRef = useRef(isDragActive);
  activeRef.current = isDragActive;

  useEffect(() => {
    let frame: number | null = null;
    let scroller: HTMLElement | null = null;
    let velocity = 0;
    let lastMove = 0;

    const stop = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      scroller = null;
      velocity = 0;
    };

    const step = () => {
      if (
        !scroller ||
        !scroller.isConnected ||
        velocity === 0 ||
        performance.now() - lastMove > STALE_MS
      ) {
        stop();
        return;
      }
      scroller.scrollTop += velocity;
      frame = requestAnimationFrame(step);
    };

    const onDragOver = (event: MouseEvent) => {
      const root = rootRef.current;
      if (!activeRef.current()) return;
      // A stale drag key outlives a modal closed mid-drag, so an unrelated drag
      // over the page must not move this list.
      if (!root || !root.contains(event.target as Node)) return;
      if (!scroller || !scroller.isConnected) scroller = findScroller(root);
      if (!scroller) return;

      const { top, bottom } = edgesOf(scroller);
      const zone = Math.max(
        MIN_ZONE,
        Math.min(MAX_ZONE, scroller.clientHeight * ZONE_RATIO)
      );
      const fromTop = event.clientY - top;
      const fromBottom = bottom - event.clientY;

      lastMove = performance.now();
      if (fromTop >= 0 && fromTop < zone) {
        velocity = -stepFor(zone - fromTop, zone);
      } else if (fromBottom >= 0 && fromBottom < zone) {
        velocity = stepFor(zone - fromBottom, zone);
      } else {
        velocity = 0;
      }

      if (velocity !== 0 && frame === null) {
        frame = requestAnimationFrame(step);
      }
    };

    // relatedTarget is null only when the pointer leaves the window entirely.
    const onDragLeave = (event: MouseEvent) => {
      if (event.relatedTarget === null) stop();
    };

    document.addEventListener('dragover', onDragOver, true);
    document.addEventListener('dragleave', onDragLeave, true);
    document.addEventListener('dragend', stop, true);
    document.addEventListener('drop', stop, true);

    return () => {
      document.removeEventListener('dragover', onDragOver, true);
      document.removeEventListener('dragleave', onDragLeave, true);
      document.removeEventListener('dragend', stop, true);
      document.removeEventListener('drop', stop, true);
      stop();
    };
  }, [rootRef]);
};
